"use client";

import React from "react";
import { Building2, ShieldCheck, Layers, CloudSun, FileText, CircleCheck as CheckCircle2, ArrowRight, HardHat, Scale } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const specifications = [{
  id: "spec-1",
  title: "Rooftop plant access",
  description: "Configured access for HVAC units, solar arrays, roof maintenance, and chiller plant replacements without compromising existing roof membranes.",
  icon: "Building2",
  bullets: ["Load-spreading sole boards and protective base interfaces", "Stair towers and compliant hop-up working platforms", "Perimeter handrail systems and kickboards", "Clear walkways for concurrent mechanical and electrical trades"]
}, {
  id: "spec-2",
  title: "External riser scaffolding",
  description: "Vertical access towers and internal/external riser systems built for building services installation, ductwork, and structural maintenance.",
  icon: "Layers",
  bullets: ["Tied access configurations adapted to high-rise building profiles", "Integrated ladder access or heavy-duty staircase options", "Secured debris netting and brick-guard integration", "Progressive lifting stages to match contractor build schedules"]
}, {
  id: "spec-3",
  title: "Weatherproof working platforms",
  description: "Temporary roof systems, clad work bays, and sheltered platforms designed to protect trades and open buildings from adverse weather conditions.",
  icon: "CloudSun",
  bullets: ["Sheeted and shrink-wrapped external weather protection", "Heavy-duty deck boards rated for multi-trade work", "Integrated rainwater drainage and runoff management", "Engineered wind loading calculations and anchoring"]
}, {
  id: "spec-4",
  title: "Safety documentation & compliance",
  description: "Complete health, safety, and operational paperwork prepared prior to site mobilization and maintained throughout the project lifecycle.",
  icon: "ShieldCheck",
  bullets: ["Site-specific Risk Assessments and Method Statements (RAMS)", "TG20:21 compliant design compliance sheets and calculations", "Handover certificates and 7-day statutory scaffold inspection logs", "Full compliance with NASC, CHAS, and Constructionline standards"]
}];
const accreditations = [{
  id: "acc-1",
  name: "NASC",
  label: "National Access & Scaffolding Confederation"
}, {
  id: "acc-2",
  name: "CHAS",
  label: "Accredited Contractor"
}, {
  id: "acc-3",
  name: "Constructionline",
  label: "Verified Member"
}];
export default function Specifications() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "Building2":
        return <Building2 className="size-5 text-primary" />;
      case "Layers":
        return <Layers className="size-5 text-primary" />;
      case "CloudSun":
        return <CloudSun className="size-5 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-primary" />;
      default:
        return <HardHat className="size-5 text-primary" />;
    }
  };
  return <section id="commercialcapabilitiesspecifications" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-foreground border-border bg-muted/50">
              Technical Specifications
            </Badge>
            <span className="text-xs font-mono text-muted-foreground uppercase tracking-wide">
              BMF Scaffolding Ltd
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
            Commercial access specifications and safety standards
          </h2>
          
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            Detailed technical access solutions engineered to strict UK safety compliance for main contractors, facility managers, and commercial projects across Berkshire, Surrey, Hampshire, London, and the South East.
          </p>
        </div>

        {/* Specification Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
          {specifications.map((spec, i) => <motion.div key={spec.id} data-index={i} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="h-full">
              <Card className="h-full border border-border bg-card text-card-foreground shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="size-10 rounded-md bg-muted flex items-center justify-center border border-border/80">
                      {getIcon(spec.icon)}
                    </div>
                    <div>
                      <CardTitle className="text-xl font-semibold text-foreground">
                        {spec.title}
                      </CardTitle>
                    </div>
                  </div>
                  <CardDescription className="text-sm text-muted-foreground leading-relaxed pt-1">
                    {spec.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="pt-2">
                  <Separator className="mb-4" />
                  <ul className="space-y-2.5">
                    {spec.bullets.map((bullet, bulletIdx) => <li key={`${spec.id}-bullet-${bulletIdx}`} data-index={bulletIdx} className="flex items-start gap-2.5 text-sm text-foreground/90">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{bullet}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Accreditations & Technical Governance Bar */}
        <div className="rounded-xl border border-border bg-muted/40 p-6 md:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <div className="flex items-center gap-2">
              <Scale className="size-4 text-primary" />
              <h3 className="text-base font-semibold text-foreground">
                Verified Health & Safety Accreditations
              </h3>
            </div>
            <p className="text-sm text-muted-foreground">
              All commercial scaffolding structures are erected under strict operational oversight in accordance with TG20:21 and SG4:15 guidance.
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {accreditations.map((acc, accIdx) => <div key={acc.id} data-index={accIdx} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-card border border-border text-xs font-medium text-foreground">
                  <FileText className="size-3.5 text-primary" />
                  <span className="font-bold">{acc.name}:</span>
                  <span className="text-muted-foreground">{acc.label}</span>
                </div>)}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto shrink-0">
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/HealthSafetyAccreditationsPage">
                View Accreditations
              </Link>
            </Button>
            <Button asChild className="w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                <span>Request Technical Quote</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}